import React, { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { EducationalResourceFormData } from "./validation.schema";
import {
  EducationalResourceStatusEnum,
  EducationalResourceTypeEnum,
} from "@/domain/educational-resource.enum";
import { CategoryOut } from "@/domain/category.interface";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export interface EducationalResourceModalPresenterProps {
  open: boolean;
  mode: "create" | "edit";
  isSubmitting: boolean;
  isLoadingData: boolean;
  onClose: () => void;
  onSubmit: () => void;
  categories?: CategoryOut[];
  isLoadingCategories?: boolean;
}

export const EducationalResourceModalPresenter: FC<
  EducationalResourceModalPresenterProps
> = ({
  open,
  mode,
  isSubmitting,
  isLoadingData,
  onClose,
  onSubmit,
  categories = [],
  isLoadingCategories = false,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useFormContext<EducationalResourceFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  const title =
    mode === "create"
      ? "Create Educational Resource"
      : "Edit Educational Resource";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="headlineSmall">{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadingData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 1,
            }}
          >
            {/* Category */}
            <Controller
              name="categoryId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Category"
                  fullWidth
                  error={!!errors.categoryId}
                  helperText={errors.categoryId?.message}
                  required
                  disabled={isLoadingCategories}
                >
                  {isLoadingCategories ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading...
                    </MenuItem>
                  ) : (
                    categories.map((category) => {
                      // Extract the name from the Text type (Record<string, string> | { def: string })
                      const categoryName =
                        typeof category.name === "object"
                          ? "en" in category.name
                            ? category.name.en
                            : "def" in category.name
                              ? category.name.def
                              : category._id
                          : category._id;

                      return (
                        <MenuItem key={category._id} value={category._id}>
                          {categoryName}
                        </MenuItem>
                      );
                    })
                  )}
                </TextField>
              )}
            />

            {/* Type */}
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  fullWidth
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  required
                >
                  {Object.values(EducationalResourceTypeEnum).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Status"
                  fullWidth
                  error={!!errors.status}
                  helperText={errors.status?.message}
                  required
                >
                  {Object.values(EducationalResourceStatusEnum).map(
                    (status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              )}
            />

            {/* Priority */}
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Priority"
                  type="number"
                  fullWidth
                  error={!!errors.priority}
                  helperText={errors.priority?.message}
                  required
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />

            {/* Attributes - Thumbnail URL */}
            <Controller
              name="attributes.thumbnailUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Thumbnail URL"
                  fullWidth
                  error={!!errors.attributes?.thumbnailUrl}
                  helperText={errors.attributes?.thumbnailUrl?.message}
                />
              )}
            />

            {/* Attributes - Duration */}
            <Controller
              name="attributes.duration"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  label="Duration (in seconds)"
                  type="number"
                  fullWidth
                  placeholder="e.g., 300 for 5 minutes"
                  error={!!errors.attributes?.duration}
                  helperText={errors.attributes?.duration?.message}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                />
              )}
            />

            <Divider sx={{ my: 1 }} />

            {/* Languages Array */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="titleMedium">Languages</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    append({
                      language: "en-us",
                      title: "",
                      description: "",
                      url: "",
                    })
                  }
                >
                  Add Language
                </Button>
              </Box>

              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="labelMedium">
                      Language {index + 1}
                    </Typography>
                    {fields.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => remove(index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {/* Language */}
                    <Controller
                      name={`languages.${index}.language`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Language"
                          fullWidth
                          error={!!errors.languages?.[index]?.language}
                          helperText={
                            errors.languages?.[index]?.language?.message
                          }
                          required
                        >
                          <MenuItem value="en-us">English (US)</MenuItem>
                          <MenuItem value="en-gb">English (UK)</MenuItem>
                          <MenuItem value="es-419">
                            Spanish (Latin America)
                          </MenuItem>
                        </TextField>
                      )}
                    />

                    {/* Title */}
                    <Controller
                      name={`languages.${index}.title`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Title"
                          fullWidth
                          error={!!errors.languages?.[index]?.title}
                          helperText={errors.languages?.[index]?.title?.message}
                          required
                        />
                      )}
                    />

                    {/* Description */}
                    <Controller
                      name={`languages.${index}.description`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          error={!!errors.languages?.[index]?.description}
                          helperText={
                            errors.languages?.[index]?.description?.message
                          }
                          required
                        />
                      )}
                    />

                    {/* URL */}
                    <Controller
                      name={`languages.${index}.url`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="URL"
                          fullWidth
                          error={!!errors.languages?.[index]?.url}
                          helperText={errors.languages?.[index]?.url?.message}
                          required
                        />
                      )}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting || isLoadingData}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : mode === "create" ? (
              "Create"
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
